import type { ConversationMessage } from '@chronosphereio/ai';
import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useRef, useState } from 'react';

type QueryAssistantProviderProps = {
  children: ReactNode;
  query: string;
  onChange: (query: string) => void;
  onRun?: () => void;
  onOpen?: () => void;
};

export type QuerySuggestion = {
  query: string;
  metadata?: { version?: string; input?: string; existingQuery?: string };
};

type QueryAssistantContextType = {
  isAssistantOpen: boolean;
  openAssistant: () => void;
  closeAssistant: () => void;
  query: string;
  onChange: (query: string) => void;
  inputValue: string;
  setInputValue: (input: string) => void;
  hasInputValue: boolean;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  suggestion: QuerySuggestion | null;
  setSuggestion: (suggestion: QuerySuggestion | null) => void;
  previousPrompts: string[];
  setPreviousPrompts: Dispatch<SetStateAction<string[]>>;
  history: ConversationMessage[];
  setHistory: Dispatch<SetStateAction<ConversationMessage[]>>;
  errorMessage: string | null;
  setErrorMessage: (errorMessage: string | null) => void;
  handleCancel: () => void;
  handleAcceptAndRun: () => void;
  handleAccept: () => void;
  handleReject: () => void;
  cancelAndSetAbortController: (controller: AbortController | null) => void;
};

const QueryAssistantContext = createContext<QueryAssistantContextType | undefined>(undefined);

export function QueryAssistantProvider(props: QueryAssistantProviderProps) {
  const { children, query, onChange, onRun, onOpen } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<QuerySuggestion | null>(null);
  const [previousPrompts, setPreviousPrompts] = useState<string[]>([]);
  const [history, setHistory] = useState<ConversationMessage[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelAndSetAbortController = useCallback((controller: AbortController | null) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = controller;
  }, []);

  const hasInputValue = Boolean(inputValue.trim());

  const handleOpen = useCallback(() => {
    onOpen?.();
    setIsOpen(true);
  }, [onOpen]);

  const handleClose = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
    setIsOpen(false);
    setInputValue('');
    setSuggestion(null);
    setPreviousPrompts([]);
    setHistory([]);
    setErrorMessage(null);
  }, []);

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoading(false);
  }, []);

  const handleAccept = useCallback(() => {
    setSuggestion(null);
  }, []);

  const handleAcceptAndRun = useCallback(() => {
    setSuggestion(null);
    onRun?.();
  }, [onRun]);

  const handleReject = useCallback(() => {
    onChange(suggestion?.metadata?.existingQuery ?? '');
    setSuggestion(null);
  }, [onChange, suggestion]);

  return (
    <QueryAssistantContext.Provider
      value={{
        isAssistantOpen: isOpen,
        openAssistant: handleOpen,
        closeAssistant: handleClose,
        query,
        onChange,
        inputValue,
        setInputValue,
        hasInputValue,
        isLoading,
        setIsLoading,
        suggestion,
        setSuggestion,
        previousPrompts,
        setPreviousPrompts,
        history,
        setHistory,
        errorMessage,
        setErrorMessage,
        handleCancel,
        handleAcceptAndRun,
        handleAccept,
        handleReject,
        cancelAndSetAbortController,
      }}
    >
      {children}
    </QueryAssistantContext.Provider>
  );
}

export function useQueryAssistant() {
  const context = useContext(QueryAssistantContext);
  if (context === undefined) {
    throw new Error('useQueryAssistant must be used within a QueryAssistantProvider');
  }
  return context;
}
